import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { getAnalysis, getSharedAnalysis } from "../lib/api";
import { TID } from "../lib/testIds";
import { RiskBadge, KV, Bullets, Section } from "../components/report/Primitives";
import Scorecard from "../components/report/Scorecard";
import { toast } from "sonner";
import {
  Loader2,
  Download,
  Share2,
  AlertTriangle,
  Building2,
  ShieldAlert,
  Target,
  TrendingUp,
  Activity,
  Users,
} from "lucide-react";

const REPORT_SECTIONS = [
  { id: "exec", n: 1, title: "Executive Summary" },
  { id: "requirement", n: 2, title: "Requirement Analysis" },
  { id: "procurement", n: 3, title: "Procurement Profile" },
  { id: "naics", n: 4, title: "NAICS & Small Business" },
  { id: "timeline", n: 5, title: "Procurement Timeline" },
  { id: "evaluation", n: 6, title: "Evaluation Criteria" },
  { id: "compliance", n: 7, title: "Compliance Review" },
  { id: "fardfars", n: 8, title: "FAR / DFARS Analysis" },
  { id: "resources", n: 9, title: "Resource Requirements" },
  { id: "competitive", n: 10, title: "Competitive Landscape" },
  { id: "risks", n: 11, title: "Opportunity Risks" },
  { id: "redflags", n: 12, title: "Hidden Red Flags" },
  { id: "effort", n: 13, title: "Proposal Effort Estimate" },
  { id: "match", n: 14, title: "Contractor Match" },
  { id: "readiness", n: 15, title: "Contractor Readiness" },
  { id: "win", n: 16, title: "Win Probability" },
  { id: "actions", n: 17, title: "Top 10 Pre-Bid Actions" },
  { id: "scorecard", n: 18, title: "GovBidIQ Scorecard" },
  { id: "pursuit", n: 19, title: "Executive Pursuit Rec." },
  { id: "final", n: 20, title: "Final Recommendation" },
];

function useAnalysisPoll(id, shared) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let timer = null;

    async function fetchOnce() {
      try {
        const fn = shared ? getSharedAnalysis : getAnalysis;
        const d = await fn(id);
        if (!mounted) return;
        setData(d);
        if (d.status === "processing") {
          timer = setTimeout(fetchOnce, 3500);
        }
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || "Failed to load");
      }
    }

    fetchOnce();
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [id, shared]);

  return { data, error };
}

function ScoreBig({ label, value, accent = "sky" }) {
  const colors = {
    sky: "text-sky-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  };
  return (
    <div className="p-5">
      <div className="tick-label mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`metric-number text-6xl leading-none ${colors[accent]}`}>{value ?? "—"}</span>
        <span className="font-mono text-sm text-slate-500">/100</span>
      </div>
    </div>
  );
}

function ProcessingScreen() {
  const steps = [
    "Parsing solicitation document",
    "Extracting Section L & M factors",
    "Mapping FAR / DFARS clauses",
    "Scoring contractor capability fit",
    "Identifying hidden red flags",
    "Calculating GovBidIQ score",
    "Generating Bid / No-Bid recommendation",
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setIdx((p) => (p + 1) % steps.length), 1800);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="max-w-2xl mx-auto px-6 py-20">
      <div className="panel-elev p-8">
        <div className="flex items-center gap-3 mb-6">
          <Loader2 className="w-5 h-5 text-sky-400 animate-spin" />
          <div className="tick-label text-sky-400">GovBidIQ Engine // Running</div>
        </div>
        <h1 className="font-display font-black text-2xl text-slate-100 mb-2">
          Analyzing your opportunity
        </h1>
        <p className="text-slate-400 text-sm mb-8">
          Claude Sonnet 4.5 is processing the solicitation. This typically takes 2-5 minutes.
        </p>
        <div className="space-y-1.5 font-mono text-[12px]">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center justify-between">
              <span className={i <= idx ? "text-slate-200" : "text-slate-600"}>
                <span className="text-slate-600">$ </span>
                {s}
                {i === idx && <span className="cursor-blink" />}
              </span>
              <span
                className={
                  i < idx ? "text-emerald-400" : i === idx ? "text-sky-400" : "text-slate-700"
                }
              >
                {i < idx ? "[OK]" : i === idx ? "[..]" : "[--]"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Report({ shared = false }) {
  const params = useParams();
  const id = shared ? params.token : params.id;
  const { data, error } = useAnalysisPoll(id, shared);

  const shareUrl = useMemo(() => {
    if (!data?.share_token) return null;
    return `${window.location.origin}/report/share/${data.share_token}`;
  }, [data]);

  const copyShare = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied to clipboard");
  };

  if (error) {
    return (
      <div className="grain min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl text-slate-100 mb-2">Unable to load report</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grain min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (data.status === "processing") {
    return (
      <div className="grain min-h-screen">
        <Navbar />
        <ProcessingScreen />
      </div>
    );
  }

  if (data.status === "failed") {
    return (
      <div className="grain min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl text-slate-100 mb-2">Analysis failed</h1>
          <p className="text-slate-400 text-sm">{data.error || "Unknown error"}</p>
        </div>
      </div>
    );
  }

  const r = data.report || {};
  const profile = data.contractor_profile || {};
  const es = r.executive_summary || {};
  const final = r.final_recommendation || {};
  const pursuit = r.executive_pursuit || {};
  const win = r.win_probability || {};

  const verdictAccent =
    final.verdict === "Strong Bid"
      ? "emerald"
      : final.verdict === "No Bid"
      ? "rose"
      : "amber";

  return (
    <div className="grain min-h-screen">
      <Navbar />

      <div data-testid={TID.reportContainer} className="max-w-[1400px] mx-auto px-6 py-8 pb-20">
        {/* HEADER */}
        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="tick-label mb-2 text-sky-400">/ Opportunity Intelligence Report</div>
            <h1 data-testid={TID.reportTitle} className="font-display font-black text-2xl sm:text-3xl text-slate-100 max-w-3xl leading-tight">
              {es.opportunity_name || "Untitled Opportunity"}
            </h1>
            <div className="font-mono text-xs text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
              <span>{[es.agency, es.solicitation_number, profile.company_name].filter(Boolean).join("  ·  ")}</span>
              {data.tier && (
                <span data-testid="report-tier-badge" className="inline-block bg-sky-500/10 border border-sky-500/40 text-sky-300 px-2 py-0.5 uppercase tracking-widest text-[9px]">
                  Tier · {data.tier}
                </span>
              )}
            </div>
          </div>
          {!shared && (
            <div className="flex items-center gap-2">
              <button
                data-testid={TID.copyShareBtn}
                onClick={copyShare}
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-300 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              <button
                data-testid={TID.exportPdfBtn}
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={3} /> Export PDF
              </button>
            </div>
          )}
        </div>

        {/* HERO SCORECARD BANNER */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--border-soft)] mb-3">
          <div className="bg-[var(--bg-secondary)]" data-testid={TID.reportOpportunityScore}>
            <ScoreBig label="Opportunity Score" value={r.opportunity_score} accent="sky" />
          </div>
          <div className="bg-[var(--bg-secondary)]" data-testid={TID.reportMatchScore}>
            <ScoreBig label="Contractor Match" value={r.contractor_match_score} accent="sky" />
          </div>
          <div className="bg-[var(--bg-secondary)]" data-testid="score-pursuit-roi">
            <ScoreBig label="Pursuit ROI" value={r.pursuit_roi_score} accent="amber" />
          </div>
          <div className="bg-[var(--bg-secondary)] p-5" data-testid={TID.reportWinProbability}>
            <div className="tick-label mb-2">Win Probability</div>
            <div className="mb-2"><RiskBadge value={win.level} /></div>
            <div className="text-xs text-slate-400 leading-relaxed line-clamp-3">{win.rationale}</div>
          </div>
          <div className="bg-[var(--bg-secondary)] p-5" data-testid={TID.reportFinalVerdict}>
            <div className="tick-label mb-2">Final Recommendation</div>
            <div className="mb-2"><RiskBadge value={final.verdict} /></div>
            <div className="font-mono text-xs text-slate-400">
              Pursuit: <span className="text-slate-200" data-testid={TID.reportPursuit}>{pursuit.recommendation || "—"}</span>
            </div>
          </div>
        </div>

        {/* Pursuit ROI Rationale */}
        {r.pursuit_roi_rationale && (
          <div className="border-l-2 border-amber-500/60 bg-[var(--bg-secondary)] p-4 mb-3" data-testid="pursuit-roi-rationale">
            <div className="tick-label text-amber-400 mb-1">/ Pursuit ROI Rationale</div>
            <p className="text-sm text-slate-200 leading-relaxed">{r.pursuit_roi_rationale}</p>
          </div>
        )}

        {/* Pursuit headline strip */}
        {(pursuit.reasoning || final.why) && (
          <div className={`border-l-2 bg-[var(--bg-secondary)] p-5 mb-10 ${
            verdictAccent === "emerald" ? "border-emerald-500" :
            verdictAccent === "rose" ? "border-rose-500" : "border-amber-500"
          }`}>
            <div className="tick-label mb-1.5">Executive Verdict</div>
            <p className="text-slate-200 text-sm leading-relaxed">{final.why || pursuit.reasoning}</p>
          </div>
        )}

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* TOC */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-20 panel p-4">
              <div className="tick-label text-sky-400 mb-3">/ Sections</div>
              <ul className="space-y-1">
                {REPORT_SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={TID.reportTocItem(s.id)}
                      className="flex items-baseline gap-3 py-1 text-[12px] text-slate-400 hover:text-sky-400 transition-colors"
                    >
                      <span className="font-mono text-[10px] text-slate-600 w-5">
                        {String(s.n).padStart(2, "0")}
                      </span>
                      <span className="leading-tight">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* CONTENT */}
          <main className="lg:col-span-9">
            {/* 1. Exec Summary */}
            <Section id="exec" n={1} title="Executive Summary">
              <div className="panel p-5 mb-4">
                <p className="text-slate-200 text-sm leading-relaxed">{es.overall_assessment}</p>
              </div>
              <div className="panel p-5">
                <KV label="Agency" value={es.agency} />
                <KV label="Solicitation #" value={es.solicitation_number} mono />
                <KV label="Opportunity" value={es.opportunity_name} />
                <KV label="Contract Type" value={es.contract_type} />
                <KV label="Contract Vehicle" value={es.contract_vehicle} />
                <KV label="Set-Aside" value={es.set_aside_status} />
                <KV label="Period of Performance" value={es.period_of_performance} />
              </div>
            </Section>

            {/* 2. Requirement Analysis */}
            <Section id="requirement" n={2} title="Requirement Analysis">
              <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)]">
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Scope</div>
                  <p className="text-sm text-slate-200 leading-relaxed">{r.requirement_analysis?.scope}</p>
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Mission Impact</div>
                  <p className="text-sm text-slate-200 leading-relaxed">{r.requirement_analysis?.mission_impact}</p>
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Deliverables</div>
                  <Bullets items={r.requirement_analysis?.deliverables} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Performance Objectives</div>
                  <Bullets items={r.requirement_analysis?.performance_objectives} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Technical Requirements</div>
                  <Bullets items={r.requirement_analysis?.technical_requirements} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Operational Requirements</div>
                  <Bullets items={r.requirement_analysis?.operational_requirements} />
                </div>
              </div>
              {r.requirement_analysis?.success_requirements && (
                <div className="panel p-5 mt-4">
                  <div className="tick-label mb-2 text-sky-400">/ What Success Requires</div>
                  <p className="text-sm text-slate-200 leading-relaxed">{r.requirement_analysis.success_requirements}</p>
                </div>
              )}
            </Section>

            {/* 3. Procurement Profile */}
            <Section id="procurement" n={3} title="Procurement Profile">
              <div className="panel p-5">
                {Object.entries(r.procurement_profile || {}).map(([k, v]) => (
                  <KV key={k} label={k.replace(/_/g, " ")} value={v} />
                ))}
              </div>
            </Section>

            {/* 4. NAICS */}
            <Section id="naics" n={4} title="NAICS & Small Business Analysis">
              <div className="panel p-5 mb-4">
                <KV label="Primary NAICS" value={r.naics_small_business?.primary_naics} mono />
                <KV label="Secondary NAICS" value={r.naics_small_business?.secondary_naics} mono />
                <KV label="Size Standard" value={r.naics_small_business?.size_standard} />
                <KV label="Set-Aside" value={r.naics_small_business?.set_aside_requirements} />
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-px bg-[var(--border-soft)]">
                {Object.entries(r.naics_small_business?.evaluations || {}).map(([k, v]) => (
                  <div key={k} className="bg-[var(--bg-secondary)] p-4">
                    <div className="tick-label mb-1.5">{k}</div>
                    <div className="text-sm text-slate-200">{v}</div>
                  </div>
                ))}
              </div>
              {r.naics_small_business?.implications && (
                <div className="panel p-5 mt-4">
                  <div className="tick-label mb-2 text-sky-400">/ Implications</div>
                  <p className="text-sm text-slate-200 leading-relaxed">{r.naics_small_business.implications}</p>
                </div>
              )}
            </Section>

            {/* 5. Timeline */}
            <Section id="timeline" n={5} title="Procurement Timeline">
              <div className="panel">
                {(r.procurement_timeline || []).length === 0 && (
                  <div className="p-5 text-slate-500 text-sm italic">No timeline dates identified.</div>
                )}
                {(r.procurement_timeline || []).map((t, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[var(--border-soft)] last:border-0 items-center"
                  >
                    <div className="col-span-1">
                      {t.critical && <span className="w-2 h-2 inline-block bg-rose-500" />}
                    </div>
                    <div className="col-span-7 text-sm text-slate-200">{t.event}</div>
                    <div className="col-span-4 font-mono text-xs text-slate-400 text-right">{t.date || "TBD"}</div>
                  </div>
                ))}
              </div>
            </Section>

            {/* 6. Evaluation Criteria */}
            <Section id="evaluation" n={6} title="Evaluation Criteria Analysis">
              <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-4">
                {[
                  ["Technical Factors", r.evaluation_criteria?.technical_factors],
                  ["Past Performance", r.evaluation_criteria?.past_performance_factors],
                  ["Price Factors", r.evaluation_criteria?.price_factors],
                  ["Management", r.evaluation_criteria?.management_factors],
                  ["Staffing", r.evaluation_criteria?.staffing_factors],
                  ["Transition", r.evaluation_criteria?.transition_factors],
                ].map(([t, v]) => (
                  <div key={t} className="bg-[var(--bg-secondary)] p-5">
                    <div className="tick-label mb-2">{t}</div>
                    <Bullets items={v} />
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)]">
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2 text-sky-400">Most Important</div>
                  <Bullets items={r.evaluation_criteria?.most_important_factors} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2 text-amber-400">Likely Discriminators</div>
                  <Bullets items={r.evaluation_criteria?.likely_discriminators} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2 text-rose-400">Evaluation Risks</div>
                  <Bullets items={r.evaluation_criteria?.evaluation_risks} />
                </div>
              </div>
            </Section>

            {/* 7. Compliance */}
            <Section id="compliance" n={7} title="Compliance Review">
              <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)]">
                {Object.entries(r.compliance_review || {}).map(([k, v]) => (
                  <div key={k} className="bg-[var(--bg-secondary)] p-5">
                    <div className={`tick-label mb-2 ${k === "disqualification_risks" ? "text-rose-400" : ""}`}>
                      {k.replace(/_/g, " ")}
                    </div>
                    <Bullets items={Array.isArray(v) ? v : [v]} />
                  </div>
                ))}
              </div>
            </Section>

            {/* 8. FAR / DFARS */}
            <Section id="fardfars" n={8} title="FAR / DFARS Analysis">
              <div className="panel overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-soft)] text-left">
                      <th className="tick-label p-3">Clause</th>
                      <th className="tick-label p-3">Title</th>
                      <th className="tick-label p-3">Contractor Impact</th>
                      <th className="tick-label p-3 text-right">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(r.far_dfars_analysis || []).map((c, i) => (
                      <tr key={i} className="border-b border-[var(--border-soft)] last:border-0 align-top">
                        <td className="p-3 font-mono text-xs text-sky-400 whitespace-nowrap">{c.number}</td>
                        <td className="p-3 text-slate-200">{c.title}<div className="text-xs text-slate-500 mt-1">{c.purpose}</div></td>
                        <td className="p-3 text-slate-300 text-sm">{c.contractor_impact}</td>
                        <td className="p-3 text-right"><RiskBadge value={c.risk_level} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* 9. Resources */}
            <Section id="resources" n={9} title="Resource Requirements Assessment">
              <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-4">
                {[
                  ["Staffing", r.resource_requirements?.staffing_complexity],
                  ["Technical", r.resource_requirements?.technical_complexity],
                  ["Financial", r.resource_requirements?.financial_capacity],
                ].map(([t, v]) => (
                  <div key={t} className="bg-[var(--bg-secondary)] p-5">
                    <div className="tick-label mb-2">{t} Complexity</div>
                    <RiskBadge value={v} />
                  </div>
                ))}
              </div>
              <div className="panel p-5">
                <KV label="Equipment" value={r.resource_requirements?.equipment_needs} />
                <KV label="Facilities" value={r.resource_requirements?.facility_needs} />
                <KV label="Management" value={r.resource_requirements?.management_requirements} />
              </div>
            </Section>

            {/* 10. Competitive */}
            <Section id="competitive" n={10} title="Competitive Landscape Assessment">
              <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-4">
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Competitive Intensity</div>
                  <RiskBadge value={r.competitive_landscape?.competitive_intensity} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Transition Risk</div>
                  <RiskBadge value={r.competitive_landscape?.transition_risk} />
                </div>
              </div>
              <div className="panel p-5">
                <KV label="Incumbent Indicators" value={r.competitive_landscape?.incumbent_indicators} />
                <KV label="Recompete Indicators" value={r.competitive_landscape?.recompete_indicators} />
                <KV label="Probable Incumbent Advantage" value={r.competitive_landscape?.probable_incumbent_advantage} />
              </div>
            </Section>

            {/* 11. Risks */}
            <Section id="risks" n={11} title="Opportunity Risk Assessment">
              <div className="space-y-3">
                {(r.opportunity_risks || []).map((rk, i) => (
                  <div key={i} className="panel p-5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <div className="font-display font-bold text-slate-100">{rk.type}</div>
                        <div className="text-sm text-slate-300 mt-1">{rk.description}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex gap-2">
                          <RiskBadge value={rk.likelihood} />
                          <RiskBadge value={rk.impact} />
                        </div>
                        <div className="tick-label">likelihood · impact</div>
                      </div>
                    </div>
                    {rk.mitigation && (
                      <div className="mt-3 pt-3 border-t border-[var(--border-soft)]">
                        <div className="tick-label mb-1 text-sky-400">/ Mitigation</div>
                        <div className="text-sm text-slate-300">{rk.mitigation}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {/* 12. Red Flags */}
            <Section id="redflags" n={12} title="Hidden Red Flags">
              <div className="space-y-3">
                {(r.hidden_red_flags || []).map((f, i) => (
                  <div key={i} className="panel p-5 border-l-2 border-rose-500/60">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-display font-bold text-slate-100 mb-1">{f.flag}</div>
                        <div className="text-sm text-slate-300">{f.why_it_matters}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* 13. Effort */}
            <Section id="effort" n={13} title="Proposal Effort Estimate">
              <div className="grid md:grid-cols-4 gap-px bg-[var(--border-soft)]">
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Complexity</div>
                  <RiskBadge value={r.proposal_effort_estimate?.complexity} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Labor Hours</div>
                  <div className="metric-number text-xl text-slate-100">{r.proposal_effort_estimate?.internal_labor_hours || "—"}</div>
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">SME Req.</div>
                  <div className="text-sm text-slate-200">{r.proposal_effort_estimate?.sme_requirements}</div>
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Resource Commit.</div>
                  <RiskBadge value={r.proposal_effort_estimate?.resource_commitment} />
                </div>
              </div>
            </Section>

            {/* 14. Match */}
            <Section id="match" n={14} title="Contractor-to-Opportunity Match">
              <div className="panel p-5 mb-4">
                <KV label="Capability Match" value={r.contractor_match_assessment?.capability_match} />
                <KV label="Past Performance" value={r.contractor_match_assessment?.past_performance_match} />
                <KV label="Geographic" value={r.contractor_match_assessment?.geographic_match} />
                <KV label="Certifications" value={r.contractor_match_assessment?.certification_match} />
                <KV label="Staffing" value={r.contractor_match_assessment?.staffing_match} />
                <KV label="Contract Vehicle" value={r.contractor_match_assessment?.contract_vehicle_match} />
                <KV label="Clearance" value={r.contractor_match_assessment?.clearance_match} />
              </div>
              <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)]">
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2 text-emerald-400">Strengths</div>
                  <Bullets items={r.contractor_match_assessment?.strengths} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2 text-rose-400">Gaps</div>
                  <Bullets items={r.contractor_match_assessment?.gaps} />
                </div>
              </div>
            </Section>

            {/* 15. Readiness */}
            <Section id="readiness" n={15} title="Contractor Readiness Assessment">
              <div className="panel p-5 mb-4 flex items-center justify-between gap-4">
                <div className="tick-label">Overall Readiness</div>
                <RiskBadge value={r.contractor_readiness?.readiness_level} />
              </div>
              <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)]">
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Barriers to Entry</div>
                  <Bullets items={r.contractor_readiness?.barriers_to_entry} />
                </div>
                <div className="bg-[var(--bg-secondary)] p-5">
                  <div className="tick-label mb-2">Teaming / Partnership Needs</div>
                  <Bullets items={r.contractor_readiness?.teaming_or_partnership_needs} />
                </div>
              </div>
            </Section>

            {/* 16. Win Probability */}
            <Section id="win" n={16} title="Win Probability Assessment">
              <div className="panel p-5">
                <div className="flex items-center gap-4 mb-3">
                  <div className="tick-label">Probability</div>
                  <RiskBadge value={win.level} />
                </div>
                <p className="text-slate-200 text-sm leading-relaxed">{win.rationale}</p>
              </div>
            </Section>

            {/* 17. Actions */}
            <Section id="actions" n={17} title="Top 10 Actions Before Bidding">
              <div className="space-y-2">
                {(r.top_actions || []).map((a, i) => (
                  <div key={i} className="panel p-4 flex items-start gap-4">
                    <div className="metric-number text-2xl text-sky-400 leading-none w-10 flex-shrink-0">
                      {String(a.priority).padStart(2, "0")}
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-100 mb-1">{a.action}</div>
                      <div className="text-sm text-slate-400">{a.rationale}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* 18. Scorecard */}
            <Section id="scorecard" n={18} title="GovBidIQ Scorecard">
              <Scorecard scorecard={r.scorecard} />
            </Section>

            {/* 19. Pursuit */}
            <Section id="pursuit" n={19} title="Executive Pursuit Recommendation">
              <div className="panel p-6 flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex-shrink-0">
                  <Target className="w-10 h-10 text-sky-400 mb-3" strokeWidth={1.4} />
                  <RiskBadge value={pursuit.recommendation} />
                </div>
                <p className="text-slate-200 text-sm leading-relaxed flex-1">{pursuit.reasoning}</p>
              </div>
            </Section>

            {/* 20. Final */}
            <Section id="final" n={20} title="Final Recommendation">
              <div className={`panel p-6 border-l-2 ${
                verdictAccent === "emerald" ? "border-emerald-500" :
                verdictAccent === "rose" ? "border-rose-500" : "border-amber-500"
              }`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="tick-label">Verdict</div>
                  <RiskBadge value={final.verdict} />
                </div>
                <p className="text-slate-200 text-sm leading-relaxed mb-6">{final.why}</p>

                <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)]">
                  <div className="bg-[var(--bg-primary)] p-5">
                    <div className="tick-label text-emerald-400 mb-2">Key Strengths</div>
                    <Bullets items={final.key_strengths} />
                  </div>
                  <div className="bg-[var(--bg-primary)] p-5">
                    <div className="tick-label text-rose-400 mb-2">Key Concerns</div>
                    <Bullets items={final.key_concerns} />
                  </div>
                  <div className="bg-[var(--bg-primary)] p-5">
                    <div className="tick-label text-sky-400 mb-2">Immediate Next Actions</div>
                    <Bullets items={final.immediate_next_actions} />
                  </div>
                </div>
              </div>
            </Section>

            {/* Disclaimer */}
            <div className="mt-12 border-t border-[var(--border-soft)] pt-6">
              <p className="text-xs text-slate-500 leading-relaxed">
                <strong className="text-slate-400">Disclaimer.</strong> This report is an AI-assisted
                decision-support tool intended to support government contracting opportunity analysis.
                It does not constitute legal advice, procurement consulting services, business advice,
                or a guarantee of award success. Users remain responsible for independent review and
                business decisions.
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
